<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import * as Sentry from '@sentry/vue';
import { useMapGetter } from 'dashboard/composables/store';
import Modal from 'dashboard/components/Modal.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const showModal = computed({
  get() {
    return props.show;
  },
  set(value) {
    if (!value) {
      emit('close');
    }
  },
});

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const description = ref('');
const isSubmitting = ref(false);

const handleClose = () => {
  description.value = '';
  isSubmitting.value = false;
  emit('close');
};

const handleSubmit = async () => {
  if (!description.value.trim()) {
    useAlert(t('BUG_REPORT.VALIDATION_ERROR'));
    return;
  }

  isSubmitting.value = true;
  try {
    const feedback = {
      name: currentUser.value.available_name || currentUser.value.name,
      email: currentUser.value.email,
      message: description.value,
    };
    const options = {
      includeReplay: true,
    };
    Sentry.captureFeedback(feedback, options);
    useAlert(t('BUG_REPORT.SUCCESS'));
    handleClose();
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Modal v-model:show="showModal" @close="handleClose">
    <div class="p-4">
      <h2 class="text-lg font-semibold text-n-slate-12 mb-3">
        {{ t('BUG_REPORT.TITLE') }}
      </h2>

      <form class="!p-0" @submit.prevent="handleSubmit">
        <div class="mb-3">
          <label
            for="bug-description"
            class="block text-sm text-n-slate-11 mb-1.5"
          >
            {{ t('BUG_REPORT.DESCRIPTION_LABEL') }}
          </label>
          <textarea
            id="bug-description"
            v-model="description"
            :placeholder="t('BUG_REPORT.DESCRIPTION_PLACEHOLDER')"
            class="w-full px-2.5 py-2 border border-slate-300 rounded text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y min-h-[100px]"
            :disabled="isSubmitting"
          />
        </div>

        <div class="flex justify-end gap-2">
          <button
            type="button"
            class="px-3 py-1.5 text-sm text-n-slate-12 bg-n-slate-3 hover:bg-n-slate-4 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            :disabled="isSubmitting"
            @click="handleClose"
          >
            {{ t('BUG_REPORT.CANCEL') }}
          </button>
          <button
            type="submit"
            class="px-3 py-1.5 text-sm !text-white bg-n-blue-8 hover:bg-n-blue-9 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            :disabled="isSubmitting || !description.trim()"
          >
            {{
              isSubmitting ? t('BUG_REPORT.SUBMITTING') : t('BUG_REPORT.SUBMIT')
            }}
          </button>
        </div>
      </form>
    </div>
  </Modal>
</template>
